import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  MapPin,
  DollarSign,
  Building2,
  Briefcase,
  CheckSquare,
  Clock,
  ArrowRight,
  Link as LinkIcon,
  Loader2,
  ChevronRight,
  Megaphone,
  Instagram,
  Check,
  X,
  Sparkles,
  AlertTriangle,
  FileText,
  User,
  Phone,
  GraduationCap,
} from 'lucide-react';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { jobService, authService, candidateService } from '../services';
import { useBannerRotation } from '../hooks/useBannerRotation';
import JobCard from '../components/JobCard';
import type { Job } from '../types';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { api } from '../lib/api';
import { cleanText, formatList, formatDescription, normalizeTitle } from '../utils/sanitize';
import { assetUrl, jobPublicUrl } from '../lib/url';

const PAINEL_URL = import.meta.env.VITE_PAINEL_URL || 'https://vagasbrusque.com.br';
const RELATED_JOBS_CYCLE_PREFIX = 'vb_related_jobs_cycle_v1';

interface JobsCycleState {
  signature: string;
  order: number[];
  cursor: number;
}

const shuffleNumbers = (items: number[]): number[] => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const buildJobsSignature = (jobs: Job[]): string => {
  return jobs
    .map((job) => Number(job.id))
    .filter((id) => Number.isFinite(id))
    .sort((a, b) => a - b)
    .join('-');
};

const createJobsCycleState = (signature: string): JobsCycleState => {
  const ids = signature
    .split('-')
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));

  return {
    signature,
    order: shuffleNumbers(ids),
    cursor: 0,
  };
};

const readJobsCycleState = (key: string, signature: string): JobsCycleState => {
  if (typeof window === 'undefined' || !signature) return createJobsCycleState(signature);

  try {
    const stored = window.localStorage.getItem(key);
    if (!stored) return createJobsCycleState(signature);

    const parsed = JSON.parse(stored) as JobsCycleState;
    const expectedIds = signature
      .split('-')
      .map((id) => Number(id))
      .sort((a, b) => a - b)
      .join('-');
    const storedIds = Array.isArray(parsed.order) ? [...parsed.order].sort((a, b) => a - b).join('-') : '';

    if (parsed.signature !== signature || storedIds !== expectedIds) {
      return createJobsCycleState(signature);
    }

    return {
      signature,
      order: parsed.order,
      cursor: Number.isFinite(parsed.cursor) ? parsed.cursor : 0,
    };
  } catch {
    return createJobsCycleState(signature);
  }
};

const writeJobsCycleState = (key: string, state: JobsCycleState) => {
  if (typeof window === 'undefined' || !state.signature) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Sem localStorage, o embaralhamento atual ainda evita lista fixa.
  }
};

const pickRotatingJobs = (jobs: Job[], keySuffix: string, count: number): Job[] => {
  if (!jobs || jobs.length === 0) return [];

  const signature = buildJobsSignature(jobs);
  if (!signature) return jobs.slice(0, count);

  const key = `${RELATED_JOBS_CYCLE_PREFIX}:${keySuffix}:${signature}`;
  const jobsById = new Map(jobs.map((job) => [Number(job.id), job]));
  const cycle = readJobsCycleState(key, signature);

  cycle.cursor = (cycle.cursor + count) % jobs.length;
  if (cycle.cursor === 0) {
    cycle.order = shuffleNumbers(cycle.order);
  }
  writeJobsCycleState(key, cycle);

  const orderedJobs = cycle.order
    .map((jobId) => jobsById.get(jobId))
    .filter((job): job is Job => Boolean(job));
  const sourceJobs = orderedJobs.length === jobs.length ? orderedJobs : shuffleNumbers(jobs.map((job) => Number(job.id)))
    .map((jobId) => jobsById.get(jobId))
    .filter((job): job is Job => Boolean(job));
  const selected: Job[] = [];

  for (let i = 0; i < Math.min(count, sourceJobs.length); i++) {
    selected.push(sourceJobs[(cycle.cursor + i) % sourceJobs.length]);
  }

  return selected;
};

const hasBeenActiveForAtLeastDays = (job: Job, days: number): boolean => {
  const activeDateValue = (job as any).published_at || job.created_at;
  if (!activeDateValue) return false;

  const activeDate = new Date(activeDateValue);
  if (Number.isNaN(activeDate.getTime())) return false;

  const ageMs = Date.now() - activeDate.getTime();
  return ageMs >= days * 24 * 60 * 60 * 1000;
};

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{ status?: number; message: string } | null>(null);
  const [applying, setApplying] = useState(false);
  const [relatedJobs, setRelatedJobs] = useState<Job[]>([]);
  const [otherJobs, setOtherJobs] = useState<Job[]>([]);
  const [seo, setSeo] = useState<any | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAlreadyAppliedModal, setShowAlreadyAppliedModal] = useState(false);
  const [showAuthRequiredModal, setShowAuthRequiredModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showCompanyEmail, setShowCompanyEmail] = useState(false);
  const [showConfirmApplyModal, setShowConfirmApplyModal] = useState(false);
  const [showIncompleteProfileModal, setShowIncompleteProfileModal] = useState(false);
  const [skipCompletenessCheck, setSkipCompletenessCheck] = useState(false);
  const [profileData, setProfileData] = useState<{
    missing_fields: string[];
    completeness_percentage: number;
    requirements: {
      has_resume: boolean;
      has_bio: boolean;
      has_phone: boolean;
      has_experience: boolean;
      has_education: boolean;
    };
  } | null>(null);

  // Sistema de banners com rodízio por tipo
  const { normalBanners, pickFromPool, trackClick, trackImpression } = useBannerRotation({
    rotationInterval: 15000,
  });

  // Pega 2 banners normais com rodízio
  const banners = pickFromPool(normalBanners, 4, 2);

  // Registra impressões dos banners deste detalhe (uma vez por sessão)
  const seenImpressions = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (!banners || banners.length === 0) return;
    banners.forEach((b) => {
      if (b?.id && !seenImpressions.current.has(b.id)) {
        seenImpressions.current.add(b.id);
        trackImpression(b.id);
      }
    });
  }, [banners, trackImpression]);

  // Scroll ao topo ao abrir uma vaga
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  // Remove parâmetros de tracking (fbclid, utm_*, gclid...) que vêm de compartilhamentos
  // no Instagram/Facebook/Google — mantém URL limpa e evita que sejam enviados em analytics/cache.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const trackingKeys = ['fbclid', 'gclid', 'msclkid', 'mc_cid', 'mc_eid', '_ga', 'igshid'];
    let changed = false;
    trackingKeys.forEach((k) => {
      if (url.searchParams.has(k)) {
        url.searchParams.delete(k);
        changed = true;
      }
    });
    // utm_* todos
    Array.from(url.searchParams.keys())
      .filter((k) => k.toLowerCase().startsWith('utm_'))
      .forEach((k) => {
        url.searchParams.delete(k);
        changed = true;
      });
    if (changed) {
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    }
  }, []);

  useEffect(() => {
    if (id) {
      const parsed = parseInt(id, 10);
      const slugIdMatch = id.match(/-(\d+)$/);
      const jobId = Number.isFinite(parsed) && parsed > 0
        ? parsed
        : slugIdMatch
          ? parseInt(slugIdMatch[1], 10)
          : NaN;

      if (Number.isFinite(jobId) && jobId > 0) {
        loadJob(jobId);
      } else {
        setLoadError({ status: 400, message: 'Identificador de vaga inválido.' });
        setLoading(false);
      }
    }
  }, [id]);


  const trackViewContent = (currentJob: Job) => {
    if (typeof window === 'undefined' || !currentJob?.id) return;

    const payload = {
      content_type: 'job',
      content_ids: [String(currentJob.id)],
      content_name: currentJob.title,
      content_category: currentJob.area || (currentJob as any).area_name || undefined,
      value: 0,
      currency: 'BRL',
    };

    const w = window as typeof window & {
      dataLayer?: Record<string, any>[];
      fbq?: (action: string, eventName: string, params?: Record<string, any>) => void;
    };

    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: 'ViewContent',
      ...payload,
    });

    if (typeof w.fbq === 'function') {
      w.fbq('track', 'ViewContent', payload);
    }
  };

  const loadJob = async (jobId: number, attempt: number = 1) => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await jobService.getJobById(jobId);
      setJob(data);
      trackViewContent(data);
      // Load related jobs based on area or company
      loadRelatedJobs(data);
      // Carrega metadados SEO do backend para coerência com SSR/Pré-render
      try {
        const seoResp = await api.get<any>(`/public/jobs/${jobId}/seo`);
        const anySeo = (seoResp as any)?.data || seoResp;
        if (anySeo && typeof anySeo === 'object') setSeo(anySeo);
      } catch (e) {
        // fallback silencioso
        setSeo(null);
      }
    } catch (error: any) {
      const status = error?.status as number | undefined;
      console.error('Erro ao carregar vaga:', { jobId, status, message: error?.message, attempt });

      // Retry automático 1x para erros de rede/servidor (exceto 404 e 401)
      if (attempt === 1 && status !== 404 && status !== 401) {
        await new Promise((r) => setTimeout(r, 800));
        return loadJob(jobId, 2);
      }

      if (status === 404) {
        setLoadError({ status: 404, message: 'Esta vaga não existe ou foi removida.' });
      } else if (status === 429) {
        setLoadError({ status: 429, message: 'Muitas requisições. Aguarde um instante e tente novamente.' });
      } else {
        setLoadError({
          status,
          message: error?.message || 'Não foi possível carregar a vaga. Verifique sua conexão e tente novamente.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedJobs = async (currentJob: Job) => {
    try {
      const currentAreaId = (currentJob as any).area_id;
      const currentArea = String(currentJob.area || '').toLowerCase();
      const sameAreaFilters: JobFilters = { per_page: 12 };

      if (currentAreaId) {
        sameAreaFilters.area_id = currentAreaId;
      }

      const response = await jobService.getJobs(sameAreaFilters);
      let jobs = response.data || [];

      let relatedCandidates = jobs
        .filter((j: Job) => {
          if (j.id === currentJob.id) return false;
          if (currentAreaId && (j as any).area_id === currentAreaId) return true;
          return currentArea && String(j.area || '').toLowerCase() === currentArea;
        });

      if (relatedCandidates.length < 4 && currentJob.skills && Array.isArray(currentJob.skills)) {
        const otherSkills = currentJob.skills.filter((s: string) =>
          s.toLowerCase() !== currentArea && s.toLowerCase() !== 'pcd'
        );
        if (otherSkills.length > 0) {
          const skillsResponse = await jobService.getJobs({ per_page: 30 });
          const skillsJobs = (skillsResponse.data || []).filter((j: Job) =>
            j.id !== currentJob.id &&
            !relatedCandidates.some((r) => r.id === j.id) &&
            j.skills &&
            Array.isArray(j.skills) &&
            j.skills.some((skill: string) => otherSkills.includes(skill))
          );
          relatedCandidates = [...relatedCandidates, ...skillsJobs];
        }
      }

      const related = pickRotatingJobs(
        relatedCandidates,
        `related:${currentJob.id}:${currentAreaId || currentArea || 'all'}`,
        4
      );
      setRelatedJobs(related);

      const relatedIds = new Set(related.map((j) => j.id));
      const otherResponse = await jobService.getJobs({ per_page: 100 });
      const lowApplicationCandidates = (otherResponse.data || [])
        .filter((j: Job) =>
          j.id !== currentJob.id &&
          !relatedIds.has(j.id) &&
          Number((j as any).applications_count || 0) < 30 &&
          hasBeenActiveForAtLeastDays(j, 4)
        );
      const lowApplicationJobs = pickRotatingJobs(
        lowApplicationCandidates,
        `other-low-applications:${currentJob.id}`,
        4
      );

      setOtherJobs(lowApplicationJobs);
    } catch (error) {
      console.error('Erro ao carregar vagas relacionadas:', error);
      setRelatedJobs([]);
      setOtherJobs([]);
    }
  };

  // Primeiro mostra confirmação, depois aplica de fato
  const handleApply = async () => {
    if (!job) return;

    if (!authService.isAuthenticated()) {
      setShowAuthRequiredModal(true);
      return;
    }

    // Se for empresa, não pode se candidatar
    if (authService.isCompany()) {
      alert('Empresas não podem se candidatar a vagas.');
      return;
    }

    // Skip completeness check if user chose to apply anyway
    if (skipCompletenessCheck) {
      setShowConfirmApplyModal(true);
      return;
    }

    if (authService.isCandidate()) {
      try {
        const completeness = await candidateService.getProfileCompleteness();
        if (!completeness.can_apply) {
          setProfileData({
            missing_fields: completeness.missing_fields,
            completeness_percentage: completeness.completeness_percentage,
            requirements: completeness.requirements,
          });
          setShowIncompleteProfileModal(true);
          return;
        }
      } catch (err: any) {
        // Se 401, sessão expirou
        if (err?.status === 401) {
          setShowAuthRequiredModal(true);
          return;
        }
        // Outros erros: deixa prosseguir
      }
    }
    // Mostra modal de confirmação antes de enviar
    setShowConfirmApplyModal(true);
  };

  const confirmApply = async () => {
    if (!job) return;
    setShowConfirmApplyModal(false);
    try {
      setApplying(true);

      // Se o usuário escolheu "Enviar assim mesmo", usa método simplificado
      if (skipCompletenessCheck) {
        const user = authService.getUser();
        let applicantName = user?.name || '';
        let applicantEmail = (user as any)?.email || '';

        // Se não tem email ou nome nos dados locais, tenta buscar via API
        if (!applicantEmail || !applicantName) {
          try {
            const fullUser = await authService.me();
            applicantName = fullUser?.name || applicantName;
            applicantEmail = (fullUser as any)?.email || applicantEmail;
          } catch (err) {
            console.error('Erro ao buscar dados do usuário:', err);
            // Continua com os dados que tem
          }
        }

        // Se ainda assim não tem email, não pode prosseguir
        if (!applicantEmail) {
          alert('Não foi possível obter seu email. Faça login novamente.');
          setSkipCompletenessCheck(false);
          return;
        }

        // Usa o nome que tem disponível (ou usa "Candidato" como fallback)
        applicantName = applicantName || 'Candidato';

        await jobService.applyToJobSimple(job.id, applicantName, applicantEmail);
        setShowSuccessModal(true);
        return;
      } else {
        await jobService.applyToJob(job.id, {});
      }

      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Erro ao se candidatar:', error);
      const message = error?.message?.toLowerCase() || '';
      const status = error?.status;

      // Detecta erro de autenticação (401 ou 403)
      if (status === 401 || status === 403 || message.includes('unauthenticated') || message.includes('não autorizado')) {
        // Tenta resyncar token do cookie antes de desistir
        const resynced = authService.checkSession();
        if (resynced && authService.getToken()) {
          // Token resyncado — tenta novamente automaticamente
          try {
            if (skipCompletenessCheck) {
              const user = authService.getUser();
              let applicantName = user?.name || '';
              let applicantEmail = (user as any)?.email || '';

              // Se não tem email, busca via API
              if (!applicantEmail || !applicantName) {
                const fullUser = await authService.me();
                applicantName = fullUser?.name || applicantName;
                applicantEmail = (fullUser as any)?.email || applicantEmail;
              }

              await jobService.applyToJobSimple(job.id, applicantName, applicantEmail);
            } else {
              await jobService.applyToJob(job.id, {});
            }
            setShowSuccessModal(true);
            return;
          } catch {
            // Falhou de novo — mostra modal de login
          }
        }
        setShowAuthRequiredModal(true);
        return;
      }
      // Detecta perfil incompleto retornado pelo backend (422)
      // Skip this check if user explicitly chose to apply anyway
      if (!skipCompletenessCheck && error?.status === 422 && (message.includes('perfil') || message.includes('complete'))) {
        try {
          const completeness = await candidateService.getProfileCompleteness();
          setProfileData({
            missing_fields: completeness.missing_fields,
            completeness_percentage: completeness.completeness_percentage,
            requirements: completeness.requirements,
          });
        } catch {
          setProfileData({
            missing_fields: ['bio', 'phone', 'experience_or_education'],
            completeness_percentage: 0,
            requirements: { has_resume: false, has_bio: false, has_phone: false, has_experience: false, has_education: false },
          });
        }
        setShowIncompleteProfileModal(true);
        return;
      }
      // Detecta se já está inscrito (ajuste conforme mensagem do seu backend)
      if (message.includes('já') || message.includes('already') || message.includes('inscrito') || message.includes('aplicado') || error?.status === 409) {
        setShowAlreadyAppliedModal(true);
      } else {
        alert(error?.message || 'Erro ao enviar candidatura.');
      }
    } finally {
      setApplying(false);
    }
  };

  // Modal de sucesso da candidatura
  const SuccessModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center animate-in zoom-in-95 duration-500">
        {/* Botão fechar */}
        <button
          onClick={() => {
            setShowSuccessModal(false);
            setSkipCompletenessCheck(false);
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="size-5" />
        </button>

        {/* Ícone animado */}
        <div className="relative mb-6">
          <div className="bg-gradient-to-br from-green-400 to-green-600 size-24 rounded-full flex items-center justify-center mx-auto shadow-lg animate-in zoom-in duration-700">
            <Check className="size-12 text-white" strokeWidth={3} />
          </div>
          {/* Sparkles decorativos */}
          <Sparkles className="absolute -top-2 -right-2 size-8 text-yellow-400 animate-pulse" />
          <Sparkles className="absolute -bottom-1 -left-3 size-6 text-yellow-400 animate-pulse delay-150" />
        </div>

        {/* Texto */}
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Candidatura Enviada!
        </h2>
        <p className="text-gray-600 mb-2">
          Sua candidatura para a vaga de
        </p>
        <p className="text-brand-600 font-semibold text-lg mb-4">
          {job?.title}
        </p>
        <p className="text-gray-500 text-sm mb-6">
          foi enviada com sucesso para <span className="font-medium">{job?.company?.company_name}</span>.
          <br />
          Boa sorte! 🍀
        </p>

        {/* Botões */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setShowSuccessModal(false)}
          >
            Continuar navegando
          </Button>
          <Button
            className="flex-1 bg-brand-600 hover:bg-brand-700"
            onClick={() => navigate('/candidato/inscricoes')}
          >
            Ver minhas inscrições
          </Button>
        </div>
      </div>
    </div>
  );

  // Modal de já inscrito (mensagem divertida)
  const AlreadyAppliedModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center animate-in zoom-in-95 duration-500">
        {/* Botão fechar */}
        <button
          onClick={() => {
            setShowAlreadyAppliedModal(false);
            setSkipCompletenessCheck(false);
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="size-5" />
        </button>

        {/* Ícone animado */}
        <div className="relative mb-6">
          <div className="bg-gradient-to-br from-amber-400 to-orange-500 size-24 rounded-full flex items-center justify-center mx-auto shadow-lg animate-in zoom-in duration-700">
            <span className="text-4xl">🔥</span>
          </div>
          {/* Sparkles decorativos */}
          <Sparkles className="absolute -top-2 -right-2 size-8 text-amber-400 animate-pulse" />
          <Sparkles className="absolute -bottom-1 -left-3 size-6 text-orange-400 animate-pulse delay-150" />
        </div>

        {/* Texto divertido */}
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          Eita, ansioso(a)? 😄
        </h2>
        <p className="text-gray-600 mb-4">
          Pelo visto você quer <span className="font-semibold text-brand-600">muito</span> essa vaga!
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-amber-800 text-sm">
            <span className="font-semibold">Calma aí!</span> 🧘‍♂️
            <br />
            Você já se candidatou para esta vaga.
            <br />
            A empresa <span className="font-medium">{job?.company?.company_name}</span> está analisando seu currículo.
          </p>
        </div>
        <p className="text-gray-500 text-sm mb-6">
          Enquanto isso, que tal explorar outras vagas incríveis? 🚀
        </p>

        {/* Botões */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              setShowAlreadyAppliedModal(false);
              setSkipCompletenessCheck(false);
            }}
          >
            Entendi!
          </Button>
          <Button
            className="flex-1 bg-brand-600 hover:bg-brand-700"
            onClick={() => {
              setShowAlreadyAppliedModal(false);
              navigate('/vagas');
            }}
          >
            Ver outras vagas
          </Button>
        </div>
      </div>
    </div>
  );

  // Modal para usuário não autenticado
  const AuthRequiredModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center animate-in zoom-in-95 duration-500">
        <button
          onClick={() => {
            setShowAuthRequiredModal(false);
            setSkipCompletenessCheck(false);
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="size-5" />
        </button>

        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Crie sua conta para se candidatar</h2>
          <p className="text-gray-600">
            Para enviar seu currículo e acompanhar suas inscrições, faça login ou crie sua conta gratuita.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              const currentUrl = encodeURIComponent(window.location.href);
              window.location.href = `${PAINEL_URL}/login?redirect=${currentUrl}`;
            }}
          >
            Fazer login
          </Button>
          <Button
            className="flex-1 bg-brand-600 hover:bg-brand-700"
            onClick={() => {
              window.location.href = `${PAINEL_URL}/cadastro`;
            }}
          >
            Criar conta Grátis
          </Button>
        </div>
      </div>
    </div>
  );

  // Modal para perfil incompleto
  const IncompleteProfileModal = () => {
    if (!profileData) return null;
    const { completeness_percentage, requirements } = profileData;

    const items = [
      { key: 'bio', label: 'Adicionar uma descrição sobre você', done: requirements.has_bio, icon: User },
      { key: 'phone', label: 'Adicionar seu telefone', done: requirements.has_phone, icon: Phone },
      { key: 'experience', label: 'Adicionar experiência profissional', done: requirements.has_experience, icon: Briefcase },
      { key: 'education', label: 'Adicionar formação acadêmica', done: requirements.has_education, icon: GraduationCap },
      { key: 'resume', label: 'Fazer upload do currículo em PDF', done: requirements.has_resume, icon: FileText },
    ];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center animate-in zoom-in-95 duration-500">
          {/* Botão fechar */}
          <button
            onClick={() => {
              setShowIncompleteProfileModal(false);
              setSkipCompletenessCheck(false);
            }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <X className="size-5" />
          </button>

          {/* Ícone animado */}
          <div className="relative mb-6">
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 size-24 rounded-full flex items-center justify-center mx-auto shadow-lg animate-in zoom-in duration-700">
              <AlertTriangle className="size-12 text-white" strokeWidth={2.5} />
            </div>
            <Sparkles className="absolute -top-2 -right-2 size-8 text-amber-400 animate-pulse" style={{ zIndex: 0 }} />
            <Sparkles className="absolute -bottom-1 -left-3 size-6 text-orange-400 animate-pulse delay-150" style={{ zIndex: 0 }} />
          </div>

          {/* Título */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Perfil Incompleto
          </h2>
          <p className="text-gray-600 mb-4">
            Complete seu perfil para se candidatar a vagas
          </p>

          {/* Barra de progresso */}
          <div className="mb-5">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-500">Completude do perfil</span>
              <span className="font-semibold text-amber-600">{completeness_percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700"
                style={{ width: `${completeness_percentage}%` }}
              />
            </div>
          </div>

          {/* Checklist */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5 text-left">
            <p className="text-sm font-semibold text-gray-700 mb-3">
              Para se candidatar, você precisa de:
            </p>
            <p className="text-xs text-gray-500 mb-3">
              Currículo em PDF <span className="font-medium">OU</span> descrição + telefone + experiência/formação
            </p>
            <ul className="space-y-2.5">
              {items.map(({ key, label, done, icon: Icon }) => (
                <li key={key} className="flex items-center gap-3">
                  <div className={`size-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    done
                      ? 'bg-green-100 text-green-600'
                      : 'bg-red-100 text-red-500'
                  }`}>
                    {done ? (
                      <Check className="size-3.5" strokeWidth={3} />
                    ) : (
                      <X className="size-3.5" strokeWidth={3} />
                    )}
                  </div>
                  <Icon className={`size-4 flex-shrink-0 ${done ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className={`text-sm ${done ? 'text-green-700 line-through' : 'text-gray-700'}`}>
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Botões */}
          <div className="flex flex-col gap-3">
            <Button
              className="w-full bg-brand-600 hover:bg-brand-700"
              onClick={() => navigate('/candidato/perfil')}
            >
              Completar meu perfil
              <ArrowRight className="size-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setSkipCompletenessCheck(true);
                setShowIncompleteProfileModal(false);
                // Vai direto para confirmação de candidatura
                setShowConfirmApplyModal(true);
              }}
            >
              Enviar candidatura assim mesmo
            </Button>
            <Button
              variant="ghost"
              className="w-full text-gray-500"
              onClick={() => {
                setShowIncompleteProfileModal(false);
                setSkipCompletenessCheck(false);
              }}
            >
              Voltar
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (err) {
        console.error('Fallback: Erro ao copiar link:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleWhatsAppContact = () => {
    if (!job) return;
    // Best-effort: tracking nao bloqueia o link.
    jobService.recordContactClick(job.id, 'whatsapp').catch(() => {});
    const phone = job.whatsapp_number?.replace(/\D/g, '') || '';
    const message = `Olá! Vi a vaga de *${job.title}* no Vagas Brusque e gostaria de saber mais informações.`;
    window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleEmailContact = () => {
    if (!job) return;
    jobService.recordContactClick(job.id, 'email').catch(() => {});
  };

  const shareWhatsApp = () => {
    const text = `Confira esta vaga: ${job?.title} - ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
  };

  // Função para construir URL completa da imagem do banner
  const getBannerImageUrl = (imageUrl: string) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    return assetUrl(imageUrl);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatSalary = () => {
    if (!job) return 'A combinar';
    if (job.salary_min && job.salary_max) {
      return `R$ ${job.salary_min.toLocaleString('pt-BR')} - R$ ${job.salary_max.toLocaleString('pt-BR')}`;
    }
    if (job.salary_min) {
      return `A partir de R$ ${job.salary_min.toLocaleString('pt-BR')}`;
    }
    if (job.salary_max) {
      return `Até R$ ${job.salary_max.toLocaleString('pt-BR')}`;
    }
    return 'Salário a combinar';
  };

  const getContractType = () => {
    if (!job) return '';
    const types: Record<string, string> = {
      clt: 'Regime CLT',
      pj: 'Pessoa Jurídica',
      estagio: 'Estágio',
      temporario: 'Temporário',
      freelancer: 'Freelancer',
    };
    return types[job.type] || job.type;
  };

  const getModality = () => {
    if (!job) return '';
    const modalities: Record<string, string> = {
      presencial: 'Presencial',
      remoto: 'Remoto',
      hibrido: 'Híbrido',
    };
    return modalities[job.modality] || job.modality;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="size-8 animate-spin text-brand-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!job) {
    const isActuallyMissing = loadError?.status === 404;
    const title = isActuallyMissing ? 'Vaga não encontrada' : 'Não foi possível carregar a vaga';
    const message = loadError?.message || 'Tente novamente em instantes.';
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
          <p className="text-gray-600 mb-6">{message}</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {!isActuallyMissing && id && (
              <Button
                onClick={() => {
                  const parsed = parseInt(id, 10);
                  if (Number.isFinite(parsed) && parsed > 0) loadJob(parsed);
                }}
              >
                Tentar novamente
              </Button>
            )}
            <Link to="/vagas">
              <Button variant={isActuallyMissing ? 'default' : 'outline'}>Ver todas as vagas</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title={seo?.title || `${job.title} - ${job.company?.company_name || job.company?.name || 'Empresa'}`}
        description={seo?.description || (job.description || '').replace(/\s+/g, ' ').slice(0, 160)}
        canonical={seo?.canonical || jobPublicUrl(job)}
        ogImage={seo?.og_image || seo?.og_image_url || (job.company as any)?.logo_url || (job.company as any)?.logo || window.location.origin + '/logo-vagas.svg'}
      />
      {/* JSON-LD da vaga (prioriza o gerado pelo backend) */}
      {seo?.jsonld ? (
        <JsonLd id={`jsonld-job-${job.id}`} data={seo.jsonld} />
      ) : (
        (() => {
          const orgName = job.company?.company_name || (job.company as any)?.name || 'Empresa';
          const addressLocality = job.city || 'Brusque';
          const addressRegion = job.state || 'SC';
          const employmentMap: Record<string, string> = {
            clt: 'FULL_TIME',
            pj: 'CONTRACTOR',
            estagio: 'INTERN',
            temporario: 'TEMPORARY',
            freelancer: 'FREELANCE',
          };
          const modalityMap: Record<string, string> = {
            remoto: 'TELECOMMUTE',
            hibrido: 'HYBRID',
            presencial: 'ON_SITE',
          };
          const json = {
            '@context': 'https://schema.org',
            '@type': 'JobPosting',
            title: job.title,
            description: job.description,
            hiringOrganization: {
              '@type': 'Organization',
              name: orgName,
              logo: (job.company as any)?.logo_url || (job.company as any)?.logo || undefined,
            },
            datePosted: job.published_at || job.created_at, // Usa published_at (data de publicação) ao invés de created_at
            validThrough: job.expires_at || undefined,
            employmentType: employmentMap[job.type] || job.type,
            jobLocationType: modalityMap[job.modality] || undefined,
            jobLocation: {
              '@type': 'Place',
              address: {
                '@type': 'PostalAddress',
                addressLocality,
                addressRegion,
                addressCountry: 'BR',
              },
            },
            applicantLocationRequirements: {
              '@type': 'Country',
              name: 'BR',
            },
            baseSalary: (job.salary_min || job.salary_max) ? {
              '@type': 'MonetaryAmount',
              currency: 'BRL',
              value: {
                '@type': 'QuantitativeValue',
                minValue: job.salary_min || undefined,
                maxValue: job.salary_max || undefined,
                unitText: 'MONTH',
              },
            } : undefined,
          } as Record<string, unknown>;
          return <JsonLd id={`jsonld-job-${job.id}`} data={json} />;
        })()
      )}
      {/* BreadcrumbList: Home > Vagas > Título */}
      <JsonLd
        id={`jsonld-breadcrumb-${job.id}`}
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: window.location.origin + '/' },
            { '@type': 'ListItem', position: 2, name: 'Vagas', item: window.location.origin + '/vagas' },
            { '@type': 'ListItem', position: 3, name: job.title, item: jobPublicUrl(job) },
          ],
        }}
      />
      <Header />

      {/* Modais */}
      {showConfirmApplyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full text-center">
            <div className="w-14 h-14 rounded-full bg-brand-100 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="size-7 text-brand-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirmar Candidatura</h3>
            <p className="text-gray-600 text-sm mb-1">Deseja se candidatar para a vaga:</p>
            <p className="font-semibold text-gray-900 mb-4">"{job?.title}"</p>
            <p className="text-xs text-gray-500 mb-6">Seu curriculo e dados do perfil serao enviados para a empresa.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => { setShowConfirmApplyModal(false); setSkipCompletenessCheck(false); }} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">
                Cancelar
              </button>
              <button onClick={confirmApply} className="px-6 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg">
                Confirmar Candidatura
              </button>
            </div>
          </div>
        </div>
      )}
      {showSuccessModal && <SuccessModal />}
      {showAlreadyAppliedModal && <AlreadyAppliedModal />}
      {showAuthRequiredModal && <AuthRequiredModal />}
      {showIncompleteProfileModal && <IncompleteProfileModal />}

      {/* Banner de título */}
      <div className="bg-gray-900 py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl font-bold text-white mb-2">Detalhes da vaga</h1>
          <nav className="flex items-center gap-2 text-brand-100 text-sm">
            <Link to="/" className="hover:text-white">home</Link>
            <ChevronRight className="size-4" />
            <Link to="/vagas" className="hover:text-white">vagas</Link>
            <ChevronRight className="size-4" />
            <span className="text-white">detalhes da vaga</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Conteúdo Principal */}
          <div className="lg:col-span-2">
            {/* Cabeçalho da Vaga */}
            <div className="flex items-start gap-4 mb-8">
              {/* Logo da empresa */}
              <div className="size-16 bg-blue-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                {(() => {
                  if ((job as any).hide_company_name) {
                    return (
                      <div className="size-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 font-bold text-2xl">?</span>
                      </div>
                    );
                  }
                  const logo = (job.company as any)?.logo || (job.company as any)?.logo_url || (job as any)?.company_logo;
                  return logo ? (
                    <ImageWithFallback
                      src={logo}
                      alt={job.company?.company_name || 'Empresa'}
                      className="size-full object-contain"
                    />
                  ) : (
                    <span className="text-blue-600 font-bold text-2xl">
                      {job.company?.company_name?.charAt(0) || 'E'}
                    </span>
                  );
                })()}
              </div>

              {/* Título e info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-xl font-bold text-gray-900">{normalizeTitle(job.title)}</h2>
                  <span className="text-xs font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                    #{job.id}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {(job.company?.company_name || (job as any).hide_company_name) && (
                    <span className="flex items-center gap-1">
                      <Building2 className="size-4" />
                      {(job as any).hide_company_name ? 'Empresa Confidencial' : job.company.company_name}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="size-4" />
                    {formatDate(job.published_at || job.created_at)} {/* Usa published_at ao invés de created_at */}
                  </span>
                </div>
              </div>
            </div>

            {/* Descrição / Atividades */}
            {job.description && (
              <div className="mb-8">
                <h3 className="font-bold text-gray-900 mb-4">Atividades:</h3>
                <div 
                  className="text-gray-600 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: formatDescription(job.description) }}
                />
              </div>
            )}

            {/* Requisitos */}
            {job.requirements && (
              <div className="mb-8">
                <h3 className="font-bold text-gray-900 mb-4">Pré-requisito:</h3>
                <ul className="space-y-2">
                  {formatList(job.requirements).map((req, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-600">
                      <span className="text-brand-600 mt-1">•</span>
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Benefícios */}
            {job.benefits && (
              <div className="mb-8">
                <h3 className="font-bold text-gray-900 mb-4">Benefícios:</h3>
                <ul className="space-y-2">
                  {formatList(job.benefits).map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-600">
                      <span className="text-brand-600 mt-1">•</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Seção A empresa (oculta em vagas sigilosas) */}
            {!(job as any).hide_company_name && (job.company?.description || job.company?.city || job.company?.address) && (
              <div className="bg-white rounded-lg border p-6 mb-6">
                <h3 className="text-gray-500 text-sm mb-2">A empresa</h3>
                <h4 className="font-bold text-gray-900 text-lg mb-4">{job.company?.company_name}</h4>

                {/* Localização da empresa */}
                {(job.company?.city || job.company?.address) && (
                  <div className="mb-4 space-y-1">
                    {job.company?.city && job.company?.state && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="size-4 text-brand-600" />
                        <span>{job.company.city} - {job.company.state}</span>
                      </div>
                    )}
                    {job.company?.address && (
                      <div className="flex items-center gap-2 text-gray-600 ml-6">
                        <span>{job.company.address}</span>
                      </div>
                    )}
                    {(job.company as any)?.zip_code && (
                      <div className="flex items-center gap-2 text-gray-500 text-sm ml-6">
                        <span>CEP: {(job.company as any).zip_code}</span>
                      </div>
                    )}
                  </div>
                )}

                {job.company?.description && (
                  <div className="text-gray-600 whitespace-pre-line">
                    {cleanText(job.company.description)}
                  </div>
                )}
              </div>
            )}
            <div className="bg-white rounded-lg border p-6 sticky top-24">
              {/* Informações da vaga */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="size-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="size-5 text-gray-600" />
                  </div>
                  <span className="text-gray-700">{formatSalary()}</span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="size-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <MapPin className="size-5 text-gray-600" />
                  </div>
                  <span className="text-gray-700">
                    {job.company?.city && job.company?.state
                      ? `${job.company.city} - ${job.company.state}`
                      : job.location || 'Brusque'}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="size-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="size-5 text-gray-600" />
                  </div>
                  <span className="text-gray-700">{getContractType()}</span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="size-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <CheckSquare className="size-5 text-gray-600" />
                  </div>
                  <span className="text-gray-700">{getModality()}</span>
                </div>

                {(job as any).published_at && (
                  <div className="flex items-center gap-3">
                    <div className="size-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Clock className="size-5 text-gray-600" />
                    </div>
                    <span className="text-gray-700 text-sm">
                      Publicada em {new Date((job as any).published_at).toLocaleDateString('pt-BR')}
                      {(job as any).expires_at && (
                        <span className="text-gray-500"> - Expira em {new Date((job as any).expires_at).toLocaleDateString('pt-BR')}</span>
                      )}
                    </span>
                  </div>
                )}

                {job.is_pcd && (
                  <div className="flex items-center gap-3">
                    <div className="size-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <CheckSquare className="size-5 text-brand-600" />
                    </div>
                    <span className="text-gray-700">Vaga para PCD</span>
                  </div>
                )}
              </div>


              {/* Botões de candidatura */}
              <div className="flex flex-col gap-2 mb-6">
                <Button
                  size="lg"
                  className="w-full bg-[#87ca1c] hover:bg-brand-700"
                  onClick={handleApply}
                  disabled={applying}
                >
                  {applying ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar meu currículo
                      <ArrowRight className="size-4 ml-2" />
                    </>
                  )}
                </Button>

                {/* Botão WhatsApp - só aparece se a empresa cadastrou número */}
                {job.whatsapp_number && (
                  <Button
                    size="lg"
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleWhatsAppContact}
                  >
                    <svg className="size-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Chamar no WhatsApp
                  </Button>
                )}

                {/* Botão Enviar e-mail para empresa */}
                {(job.company as any)?.email && !(job as any).hide_company_name && (
                  <Button
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => { handleEmailContact(); setShowCompanyEmail(!showCompanyEmail); }}
                  >
                    {showCompanyEmail ? (
                      <a href={`mailto:${(job.company as any).email}`} className="text-white hover:underline flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                        {(job.company as any).email}
                      </a>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4 mr-2"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                        Enviar e-mail
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Compartilhar */}
              <div className="border-t pt-4">
                <p className="text-sm text-gray-500 mb-3">Compartilhe</p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyLink}
                    className="flex items-center gap-2"
                  >
                    {linkCopied ? (
                      <>
                        <Check className="size-4" />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <LinkIcon className="size-4" />
                        Copiar link
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={shareWhatsApp}
                    className="text-green-600 hover:text-green-700"
                  >
                    <svg className="size-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={shareFacebook}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <svg className="size-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Banners */}
        {normalBanners.length > 0 && banners.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {banners.map((banner, index) => (
              <a
                key={`detail-banner-${banner.id}-${index}`}
                href={banner.link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick(banner.id)}
                className={`block overflow-hidden rounded-lg ${index > 0 ? 'hidden md:block' : ''}`}
              >
                <img
                  src={getBannerImageUrl(banner.image_url)}
                  alt={banner.title}
                  className="w-full h-full object-contain transition-transform duration-300 hover:scale-[1.03] bg-gray-100"
                />
              </a>
            ))}
          </div>
        )}

        {/* Vagas Relacionadas */}
        {relatedJobs.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-center mb-8">
              Vagas <span className="text-[#87ca1c]">relacionadas</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedJobs.map((relatedJob) => (
                <JobCard key={relatedJob.id} job={relatedJob} showMarketingBadges={false} />
              ))}
            </div>
          </div>
        )}

        {/* Outras Vagas */}
        {otherJobs.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-center mb-8">
              Outras <span className="text-[#87ca1c]">vagas</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {otherJobs.map((otherJob) => (
                <JobCard key={otherJob.id} job={otherJob} showMarketingBadges={false} />
              ))}
            </div>
            <div className="mt-8 flex justify-center">
              <Button
                size="lg"
                className="w-full max-w-md bg-brand-600 hover:bg-brand-700 text-white font-bold text-base md:text-lg py-6"
                onClick={() => {
                  window.location.href = 'https://vagasbrusque.com.br/';
                }}
              >
                Ver TODAS as VAGAS
                <ArrowRight className="size-5 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
